import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { fetchVehiclesByCustomer, createVehicle, updateVehicle } from '@/lib/api/vehicleApi';
import type { CustomerVehicle } from '@/lib/customers/types';

interface CreateVehicleInput {
  customer_id?: string | null;
  year: number;
  make: string;
  model: string;
  engine_size?: string | null;
  vin?: string | null;
  license_plate?: string | null;
}

interface UpdateVehicleInput extends Partial<CreateVehicleInput> {
  id: string;
}

/**
 * Hook for fetching vehicles for a customer.
 * Uses vehicle-read edge function for decrypted VIN/plate.
 */
export function useCustomerVehicles(customerId: string | undefined) {
  const { activeShop } = useAuth();

  return useQuery({
    queryKey: ['customer-vehicles', customerId],
    queryFn: async (): Promise<CustomerVehicle[]> => {
      if (!customerId || !activeShop?.id) return [];

      const vehicles = await fetchVehiclesByCustomer(activeShop.id, customerId);
      return vehicles as unknown as CustomerVehicle[];
    },
    enabled: !!customerId && !!activeShop?.id,
  });
}

/**
 * Hook for vehicle CRUD operations.
 * Uses vehicle-write edge function for encrypted writes.
 */
export function useVehicleMutations() {
  const { activeShop } = useAuth();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: async (input: CreateVehicleInput): Promise<{ id: string }> => {
      if (!activeShop?.id) throw new Error('No active shop');

      const vehicle_id = await createVehicle({
        shop_id: activeShop.id,
        customer_id: input.customer_id,
        year: input.year,
        make: input.make,
        model: input.model,
        engine_size: input.engine_size,
        vin: input.vin,
        license_plate: input.license_plate,
      });

      return { id: vehicle_id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['customer'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (input: UpdateVehicleInput): Promise<{ id: string }> => {
      if (!activeShop?.id) throw new Error('No active shop');
      const { id, ...updates } = input;

      const vehicle_id = await updateVehicle({
        vehicle_id: id,
        shop_id: activeShop.id,
        ...updates,
      });

      return { id: vehicle_id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['customer'] });
    },
  });

  // linkVehicleToCustomer stays direct — only updates customer_id (Class 5, not encrypted)
  const linkToCustomerMutation = useMutation({
    mutationFn: async ({ vehicleId, customerId }: { vehicleId: string; customerId: string }): Promise<CustomerVehicle> => {
      const { data, error } = await supabase
        .from('vehicles')
        .update({ customer_id: customerId })
        .eq('id', vehicleId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data.customer_id) {
        queryClient.invalidateQueries({ queryKey: ['customer-vehicles', data.customer_id] });
        queryClient.invalidateQueries({ queryKey: ['customer', data.customer_id] });
      }
    },
  });

  return {
    createVehicle: createMutation.mutateAsync,
    updateVehicle: updateMutation.mutateAsync,
    linkVehicleToCustomer: linkToCustomerMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isLinking: linkToCustomerMutation.isPending,
  };
}
